(() => {
  if (customElements.get('t26-ai-briefing')) return;

  const T26_TYPING_DURATION = 3000;

  class T26AiBriefing extends HTMLElement {
    connectedCallback() {
      if (this.t26Connected) return;

      this.t26Source = this.querySelector('[data-t26-ai-source]');
      this.t26Summary = this.querySelector('[data-t26-ai-summary]');
      this.t26Typing = this.querySelector('[data-t26-ai-typing]');
      this.t26Viewport = this.querySelector('[data-t26-ai-viewport]');
      this.t26Toggle = this.querySelector('[data-t26-ai-toggle]');
      this.t26Dialog = this.querySelector('[data-t26-ai-dialog]');

      if (!this.t26Source || !this.t26Summary || !this.t26Typing || !this.t26Viewport || !this.t26Toggle) return;

      this.t26Connected = true;
      // T26: Preserve paragraph boundaries in the plain-text preview; keep the original HTML in the dialog.
      const paragraphs = Array.from(this.t26Source.querySelectorAll('p'), (paragraph) => paragraph.textContent);
      this.t26Text = (paragraphs.join(' ') || this.t26Source.textContent).replace(/\s+/g, ' ').trim();
      this.t26Summary.textContent = this.t26Text;
      this.t26Viewport.hidden = !this.t26Text;
      this.t26BoundToggle = this.t26HandleToggle.bind(this);
      this.t26Toggle.addEventListener('click', this.t26BoundToggle);

      if (this.t26Dialog && typeof this.t26Dialog.showModal === 'function') {
        this.querySelector('[data-t26-ai-dialog-content]').append(this.t26Source);
        this.t26Toggle.setAttribute('aria-controls', this.t26Dialog.id);
        this.t26Toggle.setAttribute('aria-haspopup', 'dialog');
        this.t26BoundDialogClick = (event) => {
          if (event.target.closest('[data-t26-ai-close]')) this.t26Dialog.close();
          if (event.target !== this.t26Dialog) return;
          const rect = this.t26Dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
            this.t26Dialog.close();
          }
        };
        this.t26BoundClose = () => {
          if (this.isConnected) this.t26Toggle.focus({ preventScroll: true });
        };
        this.t26Dialog.addEventListener('click', this.t26BoundDialogClick);
        this.t26Dialog.addEventListener('close', this.t26BoundClose);
      }

      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = navigator.connection && navigator.connection.saveData === true;

      if (!this.t26Text || reducedMotion || saveData || this.dataset.t26Typed === 'true') return;

      this.t26TypingNode = document.createTextNode('');
      this.t26Typing.replaceChildren(this.t26TypingNode);
      this.classList.add('is-typing');

      if (typeof IntersectionObserver === 'undefined') {
        this.t26StartTyping();
        return;
      }

      this.t26Observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.t26Observer.disconnect();
        this.t26Observer = null;
        this.t26StartTyping();
      }, { rootMargin: '160px 0px', threshold: 0.01 });
      this.t26Observer.observe(this);
    }

    disconnectedCallback() {
      if (this.t26BoundToggle && this.t26Toggle) {
        this.t26Toggle.removeEventListener('click', this.t26BoundToggle);
      }
      this.t26FinishTyping();
      if (this.t26Dialog) {
        this.t26Dialog.removeEventListener('click', this.t26BoundDialogClick);
        this.t26Dialog.removeEventListener('close', this.t26BoundClose);
        if (this.t26Dialog.open) this.t26Dialog.close();
      }
      this.t26Connected = false;
    }

    t26StartTyping() {
      if (!this.t26TypingNode || this.dataset.t26Typed === 'true') return;

      this.t26StartedAt = null;
      const typeFrame = (timestamp) => {
        if (this.t26StartedAt === null) this.t26StartedAt = timestamp;

        const progress = Math.min((timestamp - this.t26StartedAt) / T26_TYPING_DURATION, 1);
        const visibleLength = Math.floor(this.t26Text.length * progress);
        this.t26TypingNode.nodeValue = this.t26Text.slice(0, visibleLength);

        if (progress < 1) {
          this.t26Frame = requestAnimationFrame(typeFrame);
        } else {
          this.t26FinishTyping();
        }
      };

      this.t26Frame = requestAnimationFrame(typeFrame);
    }

    t26FinishTyping() {
      if (this.t26Observer) {
        this.t26Observer.disconnect();
        this.t26Observer = null;
      }
      if (this.t26Frame) {
        cancelAnimationFrame(this.t26Frame);
        this.t26Frame = null;
      }
      if (this.t26TypingNode) this.t26TypingNode.nodeValue = this.t26Text;

      this.dataset.t26Typed = 'true';
      this.classList.remove('is-typing');
    }

    t26HandleToggle(event) {
      if (this.classList.contains('is-typing')) this.t26FinishTyping();
      if (!this.t26Dialog || typeof this.t26Dialog.showModal !== 'function') return;
      event.preventDefault();
      if (!this.t26Dialog.open) {
        this.t26Dialog.showModal();
        this.querySelector('[data-t26-ai-dialog-content]').scrollTop = 0;
      }
    }
  }

  customElements.define('t26-ai-briefing', T26AiBriefing);
})();
